/**
 * Legacy AgentSDK - Backward Compatibility Layer
 * 
 * This maintains the original AgentSDK API while using VoiceSDK v2 internally.
 * This ensures existing integrations continue to work without changes.
 */

import { VoiceSDK_v2 } from '../index.js';

export class AgentSDK {
  constructor(config) {
    console.log('🚀 AgentSDK v2.1.8 initialized with config:', config);
    this.config = config;
    this.voiceSDK = null;
    this.isConnected = false;
    this.isListening = false;
    this.wasListening = false; // Track if we were listening before disconnect
    this._resumeInfo = null; // Store resume info if available
    
    // Legacy callback properties
    this.onConnected = () => {};
    this.onDisconnected = () => {};
    this.onError = (error) => console.error('SDK Error:', error);
    this.onTranscript = (text) => {};
    this.onAgentSpeaking = (isStart) => {};
    
    // New event callbacks/hooks
    this.onConversationStart = config.onConversationStart || (() => {});
    this.onConversationEnd = config.onConversationEnd || (() => {});
    this.onBargeIn = config.onBargeIn || (() => {});
    this.onAudioStartPlaying = config.onAudioStartPlaying || (() => {});
    this.onAudioStoppedPlaying = config.onAudioStoppedPlaying || (() => {});
    this.onSubtitleDisplay = config.onSubtitleDisplay || (() => {});
    this.onUserTranscript = null;
    this.onUnsupportedAction = null;
  }

  /**
   * Check for resume state before connecting
   * This creates a temporary VoiceSDK instance just to check for resume
   * @returns {Object|null} Resume info if available, null otherwise
   */
  checkResumeBeforeConnect() {
    // Return cached result if already checked
    if (this._resumeChecked !== undefined) {
      return this._resumeInfo;
    }
    
    try {
      // Create a temporary VoiceSDK instance just to check for resume
      // Get visualAssistant from config or agentSettingsOverride (same pattern as connect())
      const visualAssistant = this.config.visualAssistant || 
                              this.config.agentSettingsOverride?.visualAssistant;
      
      const tempSDK = new VoiceSDK_v2({
        autoReconnect: false,
        agentId: this.config.agentId,
        appId: this.config.appId,
        language: this.config.language || 'en',
        visualAssistant: visualAssistant, // Pass through visualAssistant config (with fallback to agentSettingsOverride)
        protocolVersion: 2
      });
      
      const resumeInfo = tempSDK.checkAndResume();
      
      // Mark as checked (even if resumeInfo is null)
      this._resumeChecked = true;
      
      if (resumeInfo) {
        console.log('🔄 [AGENT SDK] Found resume state before connect:', resumeInfo);
        this._resumeInfo = resumeInfo;
        return resumeInfo;
      }
      
      this._resumeInfo = null;
      return null;
    } catch (error) {
      console.warn('⚠️ [AGENT SDK] Error checking resume:', error);
      this._resumeChecked = true; // Mark as checked even on error
      this._resumeInfo = null;
      return null;
    }
  }

  async connect() {
    try {
      // Clean up existing connection if any
      if (this.voiceSDK) {
        console.log('🔌 AgentSDK: Cleaning up existing connection');
        this.voiceSDK.destroy();
        this.voiceSDK = null;
      }
      
      // Get visualAssistant from config or agentSettingsOverride (same pattern as VoiceSDK)
      const visualAssistant = this.config.visualAssistant || 
                              this.config.agentSettingsOverride?.visualAssistant;
      
      this.voiceSDK = new VoiceSDK_v2({
        websocketUrl: this.config.websocketUrl,
        autoReconnect: false,
        agentId: this.config.agentId,
        appId: this.config.appId,
        language: this.config.language || 'en',
        ...(this.config.inputFormat || {}), // Spread inputFormat fields (sampleRate, channels, bitDepth, encoding) into VoiceSDK config
        agentSettingsOverride: this.config.agentSettingsOverride, // Pass through agentSettingsOverride if provided
        visualAssistant: visualAssistant, // Pass through visualAssistant config (with fallback to agentSettingsOverride)
        protocolVersion: 2, // Use v2 protocol
        // Output format configuration (can be overridden via config)
        outputContainer: this.config.outputContainer || 'raw', // Default: raw for lower latency
        outputEncoding: this.config.outputEncoding || 'pcm', // Default: PCM
        outputSampleRate: this.config.outputSampleRate || 24000, // Default: 24 kHz (typical TTS)
        outputChannels: this.config.outputChannels || 1, // Default: mono
        outputBitDepth: this.config.outputBitDepth || 16, // Default: 16-bit
        flavor: this.config.flavor || null,
        // Shared client-tool handler map (set by TTPChatWidget so voice + text SDKs
        // both look up handlers in the same registration site).
        sharedToolHandlers: this.config.sharedToolHandlers || null,
        // Persisted in-call settings provider (voice speed / response length) —
        // read fresh at hello_ack so localStorage changes survive reconnects.
        callSettingsProvider: this.config.callSettingsProvider || null
      });
      
      // If we have stored resume info from checkResumeBeforeConnect(), use it
      // The conversationId is already stored in sessionStorage and will be sent via query param
      if (this._resumeInfo) {
        console.log('🔄 [AGENT SDK] Using stored resume info:', this._resumeInfo);
        console.log('   From:', this._resumeInfo.fromPage, '→ To:', this._resumeInfo.toPage);
      }
      
      // Set up event handlers to map to legacy callbacks
      this.voiceSDK.on('connected', () => {
        this.isConnected = true;
        this.onConnected();
      });
      
      // Also check if already connected (in case event fired before listener was attached)
      // Use a small delay to ensure event handlers are set up
      setTimeout(() => {
        if (this.voiceSDK && this.voiceSDK.isConnected && !this.isConnected) {
          this.isConnected = true;
          this.onConnected();
        }
      }, 10);
      
      this.voiceSDK.on('disconnected', (event) => {
        this.isConnected = false;
        // Check if disconnect was due to domain whitelist violation
        if (event && event.code === 1008 && event.reason && 
            (event.reason.includes('Domain not whitelisted') || 
             event.reason.includes('domain') || 
             event.reason.includes('whitelist'))) {
          const domainError = new Error('DOMAIN_NOT_WHITELISTED');
          domainError.reason = event.reason;
          domainError.code = event.code;
          this.onError(domainError);
        }
        // Call conversation end hook on disconnect (if conversation was active)
        // Use wasListening to catch cases where recordingStopped already set isListening to false
        if (this.isListening || this.wasListening) {
          console.log('🎤 [AGENT SDK] disconnected event - calling onConversationEnd hook (isListening:', this.isListening, ', wasListening:', this.wasListening, ')');
          if (this.onConversationEnd && typeof this.onConversationEnd === 'function') {
            try {
              this.onConversationEnd();
              console.log('🎤 [AGENT SDK] ✅ onConversationEnd hook called from disconnected event');
              this.wasListening = false; // Reset after calling hook
            } catch (error) {
              console.error('🎤 [AGENT SDK] ❌ Error calling onConversationEnd from disconnected:', error);
            }
          }
        }
        this.onDisconnected();
      });
      
      this.voiceSDK.on('domainError', (error) => {
        this.onError(error);
      });
      
      this.voiceSDK.on('error', (error) => {
        this.onError(error);
      });
      
      this.voiceSDK.on('message', (message) => {
        this.handleWebSocketMessage(message);
      });
      
      this.voiceSDK.on('recordingStarted', () => {
        this.isListening = true;
        this.wasListening = true; // Mark that we were listening
        // Call conversation start hook
        if (this.onConversationStart && typeof this.onConversationStart === 'function') {
          try {
            this.onConversationStart();
          } catch (error) {
            console.error('🎤 [AGENT SDK] ❌ Error calling onConversationStart:', error);
          }
        }
      });
      
      this.voiceSDK.on('recordingStopped', () => {
        console.log('🎤 [AGENT SDK] recordingStopped event received');
        const wasListening = this.isListening; // Store state before changing
        this.isListening = false;
        // Call conversation end hook if we were listening
        if (wasListening && this.onConversationEnd && typeof this.onConversationEnd === 'function') {
          try {
            console.log('🎤 [AGENT SDK] ✅ Calling onConversationEnd hook from recordingStopped');
            this.onConversationEnd();
            console.log('🎤 [AGENT SDK] ✅ onConversationEnd hook called successfully');
            this.wasListening = false; // Reset after calling hook
          } catch (error) {
            console.error('🎤 [AGENT SDK] ❌ Error calling onConversationEnd:', error);
          }
        }
      });
      
      this.voiceSDK.on('playbackStarted', () => {
        this.onAgentSpeaking(true);
        // Call audio start playing hook
        this.onAudioStartPlaying();
        // Call playback started callback if set
        if (this.onPlaybackStarted && typeof this.onPlaybackStarted === 'function') {
          this.onPlaybackStarted();
        }
      });
      
      this.voiceSDK.on('playbackStopped', () => {
        this.onAgentSpeaking(false);
        // Call audio stopped playing hook
        this.onAudioStoppedPlaying();
      });
      
      // Handle barge-in events
      this.voiceSDK.on('bargeIn', (message) => {
        this.onBargeIn(message);
      });

      // Handle transcript display events (when audio for a sentence starts playing)
      this.voiceSDK.on('transcriptDisplay', (transcriptData) => {
        console.log('📝 [AGENT SDK] transcriptDisplay event received:', transcriptData);
        // Call onTranscript with the transcript text (legacy)
        if (transcriptData && transcriptData.text) {
          console.log('📝 [AGENT SDK] Calling onTranscript with text:', transcriptData.text);
          if (this.onTranscript && typeof this.onTranscript === 'function') {
            this.onTranscript(transcriptData.text);
          } else {
            console.warn('📝 [AGENT SDK] ⚠️ onTranscript is not a function!', typeof this.onTranscript);
          }
        }
        // Call subtitle display hook with full transcript data
        this.onSubtitleDisplay(transcriptData);
      });

      // Handle user transcript events (STT interim + final text from backend)
      this.voiceSDK.on('user_transcript', (data) => {
        console.log('📝 [AGENT SDK] user_transcript event received:', data);
        if (this.onUserTranscript && typeof this.onUserTranscript === 'function') {
          this.onUserTranscript(data.text, data.final);
        }
      });
      
      // Register any pending tool handlers
      if (this._pendingToolHandlers && this._pendingToolHandlers.size > 0) {
        console.log(`🔧 AgentSDK: Registering ${this._pendingToolHandlers.size} pending tool handlers`);
        for (const [name, handler] of this._pendingToolHandlers) {
          this.voiceSDK.registerToolHandler(name, handler);
        }
      }
      
      // Connect using VoiceSDK
      await this.voiceSDK.connect();
      
    } catch (error) {
      this.onError(error);
      throw error;
    }
  }

  handleWebSocketMessage(message) {
    // Map new message format to legacy format
    // v2 SDK uses 't' field for message type instead of 'type'
    const messageType = message.t || message.type;
    
    switch (messageType) {
      case 'connected':
      case 'hello_ack':
        console.log('Session started successfully');
        break;
        
      case 'user_transcript':
        // Handled via VoiceSDK 'user_transcript' event listener -> onUserTranscript callback
        break;
        
      case 'agent_response':
        // Handle agent text response
        break;
        
      case 'barge_in':
        // Handle barge-in
        break;
        
      case 'stop_playing':
        // Handle stop playing
        break;
        
      case 'error':
        this.onError(new Error(message.message || message.error || 'Unknown error'));
        break;

      case 'show_items':
      case 'show_products':
      case 'cart_updated':
      case 'add_to_store_cart':
        if (typeof this.onUnsupportedAction === 'function') {
          this.onUnsupportedAction(messageType, message);
        }
        break;
        
      default:
        // Handle other message types
        break;
    }
  }

  async startListening(existingMediaStream = null) {
    console.log('🎤 AgentSDK: startListening() called', existingMediaStream ? '(with existing stream)' : '');
    if (this.voiceSDK) {
      try {
        console.log('🎤 AgentSDK: Starting recording...');
        const result = await this.voiceSDK.startRecording(existingMediaStream);
        // Only log success if recording actually started (not server rejection)
        if (result) {
          console.log('✅ AgentSDK: Recording started successfully');
        }
      } catch (error) {
        // Handle server rejection gracefully (don't log as error)
        if (error.isServerRejection || error.name === 'ServerRejected') {
          // Server rejection is expected - don't log as error
          throw error;
        }
        console.error('❌ AgentSDK: Failed to start recording:', error);
        throw error;
      }
    } else {
      console.error('❌ AgentSDK: No voiceSDK instance available');
      throw new Error('No voiceSDK instance available');
    }
  }

  stopListening() {
    if (this.voiceSDK) {
      this.voiceSDK.stopRecording();
    }
  }

  hangup() {
    if (this.voiceSDK) {
      this.voiceSDK.hangup();
    }
  }

  /**
   * Pause the call. Stops sending/receiving audio, closes STT/TTS on the server.
   * The WebSocket stays open and conversation history is preserved.
   * Call resumeCall() to continue the conversation.
   */
  pauseCall() {
    if (this.voiceSDK) {
      this.voiceSDK.pauseCall();
    }
  }

  /**
   * Resume a paused call. Restarts audio recording and server-side STT/TTS.
   */
  resumeCall() {
    if (this.voiceSDK) {
      this.voiceSDK.resumeCall();
    }
  }

  /**
   * Check if the call is currently paused.
   * @returns {boolean}
   */
  get isPaused() {
    return this.voiceSDK?.isPaused || false;
  }

  updateVariables(variables) {
    if (this.voiceSDK && this.isConnected) {
      // Send variables update message
      // v2 SDK uses sendMessage directly on the SDK instance
      this.voiceSDK.sendMessage({
        t: 'update_variables',
        variables
      });
    }
  }

  disconnect() {
    if (this.voiceSDK) {
      this.voiceSDK.destroy();
      this.voiceSDK = null;
    }
    this.isConnected = false;
    this.isListening = false;
  }

  /**
   * Register a handler for a client tool
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @example
   * agentSDK.registerToolHandler('getPageContent', async (params) => {
   *   return { title: document.title, url: window.location.href };
   * });
   */
  registerToolHandler(name, handler) {
    if (this.voiceSDK) {
      return this.voiceSDK.registerToolHandler(name, handler);
    }
    // Store for later registration when SDK connects
    if (!this._pendingToolHandlers) {
      this._pendingToolHandlers = new Map();
    }
    this._pendingToolHandlers.set(name, handler);
    console.log(`🔧 AgentSDK: Queued tool handler '${name}' for registration after connect`);
  }

  /**
   * Unregister a tool handler
   * @param {string} name - Tool name
   */
  unregisterToolHandler(name) {
    if (this.voiceSDK) {
      return this.voiceSDK.unregisterToolHandler(name);
    }
    if (this._pendingToolHandlers) {
      this._pendingToolHandlers.delete(name);
    }
  }

  /**
   * Check if a tool handler is registered
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasToolHandler(name) {
    if (this.voiceSDK) {
      return this.voiceSDK.hasToolHandler(name);
    }
    return this._pendingToolHandlers?.has(name) || false;
  }

  /**
   * Get all registered tool handler names
   * @returns {string[]}
   */
  getRegisteredTools() {
    if (this.voiceSDK) {
      return this.voiceSDK.getRegisteredTools();
    }
    return this._pendingToolHandlers ? Array.from(this._pendingToolHandlers.keys()) : [];
  }
}
